import {JSONSchema7} from 'json-schema';
import {get, isEqual, unset} from 'lodash';
import {JsonSchemaUtils} from './json-schema.utils';
import {DateUtils} from './date.utils';

export interface JsonInputOutputMappingLike {
    value_serialized?: string | null;
    reference_expression?: string | null;
}

export abstract class JsonUtils {
    /**
     * Marker used to indicate that a secret value is masked and should not be sent back to the API.
     * This marker is used in the serialized value of secret input/output mappings to indicate that the actual secret value is not available.
     */
    public static readonly SECRET_MASKED_MARKER = 'enc:vx:MASKED';

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static deserializeValue(serializedValue: string): any {
        return JSON.parse(serializedValue);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static serializeValue(value: any): string {
        if (value === null || value === undefined) return 'null';

        return JSON.stringify(value, (key, value) => {
            if (value instanceof Date) {
                return DateUtils.toLocalISOString(value);
            }
            return value;
        });
    }

    /**
     * Clones the given value for mutation. If the value is a primitive, it is returned as-is. If it is an object or array, a deep clone is returned.
     * Secret markers are not stripped in this method. Use `stripMaskedSecretMarkersByPaths` to strip secret markers from the cloned value.
     *
     * @param value The value to clone.
     * @returns The cloned value.
     */
    public static stripUnchangedSecretInputMappingsForUpdate<TMapping extends JsonInputOutputMappingLike>(
        currentMappings: Record<string, TMapping> | undefined,
        options: {
            isCreationMode: boolean;
            initialMappings?: Record<string, TMapping>;
            rootSchema?: JSONSchema7;
            resolveInitialSerializedValue?: (mappingKey: string, initialMapping?: TMapping) => string | undefined;
        }
    ): Record<string, TMapping> {
        const result = structuredClone(currentMappings ?? {});

        if (options.isCreationMode || !options.initialMappings || !options.rootSchema?.properties) {
            return result;
        }

        for (const mappingKey of Object.keys(result)) {
            const mapping = result[mappingKey];
            if (!mapping?.value_serialized) {
                continue;
            }

            const initialMapping = options.initialMappings[mappingKey];
            const initialSerialized = options.resolveInitialSerializedValue
                ? options.resolveInitialSerializedValue(mappingKey, initialMapping)
                : (initialMapping?.value_serialized ?? undefined);
            if (initialSerialized === undefined || initialSerialized === null) {
                continue;
            }

            const propertySchema = options.rootSchema.properties[mappingKey];
            if (!propertySchema || propertySchema === true) {
                continue;
            }

            const secretPaths = JsonSchemaUtils.collectSecretLeafPaths(propertySchema, options.rootSchema);
            if (secretPaths.length === 0) {
                continue;
            }

            const currentValue = JsonUtils.deserializeValue(mapping.value_serialized);
            const initialValue = JsonUtils.deserializeValue(initialSerialized);
            const hasRootSecretPath = secretPaths.some((path) => path.length === 0);

            // Root-level masked marker from API must never be sent back.
            if (hasRootSecretPath && currentValue === JsonUtils.SECRET_MASKED_MARKER) {
                result[mappingKey] = {} as TMapping;
                continue;
            }

            // Root-level secret scalar (path=[]): unchanged means omit value_serialized.
            if (hasRootSecretPath && isEqual(currentValue, initialValue)) {
                result[mappingKey] = {} as TMapping;
                continue;
            }

            const nestedSecretPaths = secretPaths.filter((path) => path.length > 0);
            if (nestedSecretPaths.length === 0) {
                continue;
            }

            const strippedValue = JsonUtils.cloneValueForMutation(currentValue);
            JsonUtils.stripMaskedSecretMarkersByPaths(strippedValue, nestedSecretPaths);
            JsonSchemaUtils.stripUnchangedSecretsByPaths(strippedValue, initialValue, nestedSecretPaths);

            const cleanedValue = JsonUtils.stripEmptyObjects(strippedValue);
            if (JsonUtils.isEmptyObject(cleanedValue)) {
                result[mappingKey] = {} as TMapping;
                continue;
            }

            result[mappingKey] = {
                value_serialized: JsonUtils.serializeValue(cleanedValue)
            } as TMapping;
        }

        return result;
    }

    /**
     * Clones the given value for mutation. If the value is a primitive, it is returned as-is. If it is an object or array, a deep clone is returned.
     * Secret markers are not stripped in this method. Use `stripMaskedSecretMarkersByPaths` to strip secret markers from the cloned value.
     *
     * @param value The value to clone.
     * @returns The cloned value.
     */
    public static cloneValueForMutation(value: unknown): unknown {
        if (value === null || value === undefined || typeof value !== 'object') {
            return value;
        }

        return structuredClone(value);
    }

    /**
     * Strips secret markers from the given target value at the specified paths.
     * If a secret marker is found at a path, it is removed from the target value.
     *
     * @param targetValue The target value to strip secret markers from.
     * @param paths An array of paths where secret markers should be stripped.
     */
    public static stripMaskedSecretMarkersByPaths(targetValue: unknown, paths: string[][]): void {
        if (targetValue === null || targetValue === undefined || typeof targetValue !== 'object') {
            return;
        }

        for (const path of paths) {
            const valueAtPath = get(targetValue, path);
            if (valueAtPath === JsonUtils.SECRET_MASKED_MARKER) {
                unset(targetValue, path);
            }
        }
    }

    /**
     * Recursively strips empty objects from the given value.
     * If the value is an object, it will remove any properties that are empty objects.
     * If the value is an array, it will recursively strip empty objects from its elements.
     *
     * @param value The value to strip empty objects from.
     * @returns The stripped value with empty objects removed.
     */
    public static stripEmptyObjects(value: unknown): unknown {
        if (value === null || value === undefined || typeof value !== 'object' || Array.isArray(value)) {
            return value;
        }

        const cleaned: Record<string, unknown> = {};
        for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
            const cleanedChild = this.stripEmptyObjects(child);
            if (cleanedChild === undefined) {
                continue;
            }

            if (this.isEmptyObject(cleanedChild)) {
                continue;
            }

            cleaned[key] = cleanedChild;
        }

        return cleaned;
    }

    /**
     * Checks if the given value is an empty object.
     * An empty object is defined as an object with no own enumerable properties.
     *
     * @param value The value to check.
     * @returns True if the value is an empty object, false otherwise.
     */
    public static isEmptyObject(value: unknown): boolean {
        return value !== null && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0;
    }
}
